import React from "react";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CardMedia,
    Typography,
    styled,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useNavigation } from "@hooks";
import { fontWeights } from "@themes";

// Styles for the top-level component
const Container = styled(Card, {
    shouldForwardProp: (prop) => prop !== "isLessThan375px",
})(({ theme, isLessThan375px }) => ({
    backgroundColor: theme.vars.palette.common.white,
    border: "1px solid rgba(0, 0, 0, 0.10);",
    borderRadius: 12,
    boxShadow: "var(--winvest-shadows-0)",  // "none"
    flex: 1,
    minWidth: isLessThan375px ? 300 : 355,
    position: "relative",
    width: isLessThan375px ? 300 : 355,
}));

// Styles for the header, indicating the workshop "category"
const Header = styled(CardHeader)(({ theme }) => ({
    position: "absolute",
    width: "inherit",
    ".MuiCardHeader-title": {
        ...theme.typography.callout,
        color: theme.vars.palette.common.white,
    },
}));

// Sty;es for the image displayed on the card
const Image = styled(CardMedia)(({ color }) => ({
    backgroundColor: color,
    height: "160px",
    objectPosition: "center",
    paddingLeft: "20px",
}));

// Styles for the text content, indicating topic, date and time
const ContentWrapper = styled(CardContent)(() => ({
    alignSelf: "stretch",
    backgroundColor: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "12px 16px",
    ":last-child": { paddingBottom: 12 },
}));

// Styles for the tier (e.g. "FREE")
const Tier = styled(Typography)(({ theme }) => ({
    ...theme.typography.bodyS,
    alignItems: "center",
    alignSelf: "stretch",
    background: theme.vars.palette.base.background,
    borderRadius: 32,
    color: theme.vars.palette.base.primary,
    display: "flex",
    fontWeight: fontWeights.MEDIUM,
    height: 25,
    padding: "8px 12px",
    minWidth: "fit-content",
}));

// Workshop topic and tier (e.g. "FREE")
const Details = ({ topic, tier }) => (
    <Box
        alignItems="center"
        display="flex"
        flexDirection="row"
        minHeight={25}
        justifyContent="space-between"
    >
        <Typography variant="bodyLEmphasis">{topic}</Typography>
        <Tier>
            {tier && tier.toUpperCase()}
        </Tier>
    </Box>
);

// Date and time of the workshop
const DateTime = (props) => {
    const { date, time } = props;
    const color = "#5C736C";  // UI-700

    return (
        <Box
            alignItems="center"
            display="flex"
            flexDirection="row"
            gap="8px"
            minHeight={25}
        >
            <Typography variant="bodyEmphasis" color={color}>
                {date}
            </Typography>
            <Typography variant="body" color={color}>
                {time}
            </Typography>
        </Box>
    );
};

const WorkshopCard = (props) => {
    const {
        isWorkshopPage = false,
        status = "static",
        category,
        topic,
        datetime,
        tier,
        image,
        color,
        sx,
        onClick = () => {},
     } = props;
     const { isMobileNav } = useNavigation();
     const theme = useTheme();
    // Media query that matches when screen width is 300px or less
    const isLessThan375px = useMediaQuery("(max-width: 375px)");

    return (
        <Container
            isLessThan375px={isLessThan375px}
            sx={{
                ...sx,
                ...(status === "live" && !isWorkshopPage && {
                    ...theme.hoverable,
                }),
            }}
            // Only clickable if the workshop is live (i.e., not static)
            {...(status === "live" ? { onClick } : {})}
        >
            <Header title={category} />
            <Image
                component="img"
                image={image}
                color={color}
                sx={{
                    height: isWorkshopPage
                        ? isMobileNav
                            ? "160px"
                            : "40vh"
                        : "160px"
                }}
            />
            <ContentWrapper>
                <Details topic={topic} tier={tier} />
                <DateTime {...datetime} />
            </ContentWrapper>
        </Container>
    );
};

export default WorkshopCard;
